import RequiredIndicator from "@base/ui/RequiredIndicator";
import { Selector } from "@base/ui/Selector";
import { createListCollection } from "@ark-ui/react/select";
import { useMemo } from "react";
import type { TimeSlot } from "@features/reservation/model/types";

export interface DayTimeSelectorProps {
  items: TimeSlot[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
}

const DayTimeSelector = ({
  items,
  value,
  onValueChange,
  placeholder,
  label,
  required = false,
  disabled = false,
  "aria-label": ariaLabel,
}: DayTimeSelectorProps) => {
  const collection = useMemo(
    () =>
      createListCollection<TimeSlot>({
        items,
      }),
    [items],
  );

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <span className="text-label2 text-gray-900">
          {label} {required && <RequiredIndicator />}
        </span>
      )}
      <Selector<TimeSlot>
        collection={collection}
        value={value ? [value] : []}
        onValueChange={(details) => {
          const selectedValue = details.value[0];
          if (selectedValue) {
            onValueChange(selectedValue);
          }
        }}
        disabled={disabled}
        className="w-full"
      >
        <Selector.Control size="medium" variant="outline" aria-label={ariaLabel}>
          {({ selectedItems }) => {
            const selected = selectedItems[0] as TimeSlot | undefined;
            return <span className="truncate">{selected?.label ?? placeholder ?? "Select time"}</span>;
          }}
        </Selector.Control>
        <Selector.Content className="overflow-y-auto z-10 p-2 max-h-60" usePortal={false}>
          {collection.items.map((item) => (
            <Selector.Item key={item.value} item={item} className="px-3 py-2 cursor-pointer hover:bg-gray-50">
              {({ selected }) => (
                <div className="flex gap-2 justify-between items-center">
                  <span className="text-label2 text-gray-1000">{item.label}</span>
                  {selected && <span className="text-primary-500">✓</span>}
                </div>
              )}
            </Selector.Item>
          ))}
        </Selector.Content>
      </Selector>
    </div>
  );
};

export default DayTimeSelector;
